import { Message, Modal } from '@arco-design/web-react';
import axios from 'axios';
import querystring from 'query-string';
import { v4 as uuidv4 } from 'uuid';

import i18n from '@/i18n';
import { LOGIN_PATH } from '@/routes/paths';
import { PREFIX_URL } from '@/service/constants';
import { globalErrorHandler } from '@/store/err-status';
import { getUrlSearchParamValue } from '@/utils';

import { clearTokens, getAccessToken, getRefeshToken, setAccessToken, setRefeshToken } from '../utils/useLocalStorage';
import {
  createAuthRecoveryCoordinator,
  createSessionInvalidator,
  resolveAuthMode,
  resolveForbiddenAction,
  resolveResponseStatus,
  resolveStaleRequestAction,
  shouldAttachAccessToken,
} from './auth-recovery';
import { IRequest } from './interface';
import { notifySessionChange } from './session-sync';

// 超时
const TIMEOUT = 180 * 10000;
// 刷新token的接口
export const REFRESH_TOKEN_URL = `${PREFIX_URL}/oauth/refresh`;

/**
 * toString(36)表示0-9 + 26个英文字母
 */
export function uuid(): string {
  return uuidv4();
}

const instance = axios.create({
  timeout: TIMEOUT,
});

// 请求拦截
instance.interceptors.request.use(
  (request) => {
    if (request?.url?.includes('/clusters/:cluster/')) {
      return Promise.reject({
        config: request,
        response: {
          status: 403,
          data: i18n.t('service:cluster.does.not.exist'),
        },
      });
    }

    const urlToken = getUrlSearchParamValue('token');
    const accessToken = getAccessToken();
    if (urlToken) {
      request.headers.Authorization = `Bearer ${urlToken}`;
    } else if (shouldAttachAccessToken(request.url) && !request.headers.Authorization && accessToken) {
      request.headers.Authorization = `Bearer ${accessToken}`;
    }
    (request as IRequest).__authAccessToken = accessToken;
    (request as IRequest).__errorPath ||= window.location.pathname;
    request.headers['X-Operation-ID'] = uuid();
    return request;
  },
  (err) => {
    return Promise.reject(err);
  },
);

// 返回拦截
instance.interceptors.response.use(
  (response) => {
    return response.data;
  },
  (error) => {
    if (error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError') {
      return Promise.reject(error);
    }
    const url = error.config?.url;
    const status = resolveResponseStatus(error?.response?.data?.code, error?.response?.status);
    const errorMsg =
      error?.response?.data?.error_description ||
      error?.response?.data?.message ||
      (typeof error?.response?.data === 'string' ? error?.response?.data : '');

    // TODO : 错误处理后续可以提取成一个回调函数通过配置项的形式配置不容的状态码处理方式
    switch (status) {
      case 401:
        if (resolveAuthMode(error.config, url) !== 'none') {
          const config = (error.config || {}) as IRequest;
          const currentAccessToken = getAccessToken();
          const staleAction = resolveStaleRequestAction(config.__authAccessToken ?? null, currentAccessToken);
          if (staleAction === 'reject' || (staleAction === 'retry-current' && config.__retryCount)) {
            return Promise.reject(error);
          }
          if (staleAction === 'retry-current') {
            return request({
              ...config,
              __retryCount: 1,
              headers: {
                ...config.headers,
                Authorization: `Bearer ${currentAccessToken}`,
              },
            });
          }
          // 刷新成功后业务接口仍 401：按普通错误提示，不清会话。
          if (config.__retryCount) {
            break;
          }
          return recoverRequest({
            mode: resolveAuthMode(config, url),
            hasRetried: false,
            originalError: error,
            retry: () =>
              request({
                ...config,
                __retryCount: 1,
                headers: {
                  ...config.headers,
                  Authorization: `Bearer ${getAccessToken()}`,
                },
              }),
          });
        }
        break;

      case 403:
        switch (resolveForbiddenAction(error.config?.method, resolveAuthMode(error.config, url))) {
          case 'page':
            globalErrorHandler.setError(error.config?.__errorPath || window.location.pathname, {
              status,
              message: errorMsg,
            });
            break;
          case 'dialog':
            Modal.error({
              title: i18n.t('components:no.permission'),
              content: i18n.t('components:no.access.permission.please.contact.the.administrator.to.add.permissions'),
            });
            break;
          default:
            break;
        }
        return Promise.reject({ status, message: errorMsg });

      case 404:
        // horizontalpodautoscalers 相关的 404 错误不显示提示
        if (url?.includes('horizontalpodautoscalers')) {
          return Promise.reject({ status, message: errorMsg });
        }
        break;

      default:
        break;
    }
    if (!error.config?.flag) {
      // 其他错误，默认处理，统一显示message
      errorMsg && Message.error(errorMsg);
    }
    return Promise.reject({ status, message: errorMsg });
  },
);

const invalidateSession = createSessionInvalidator({
  // 禁止在此清空全局 SWR：会让根级平台配置进入 loading，反复卸载并重挂 AppLayout。
  clearSession: () => {
    clearTokens();
    notifySessionChange();
  },
  getLocation: () => window.location,
  loginPath: LOGIN_PATH,
  redirect: (url) => {
    window.location.replace(url);
  },
  notifyLoginRequired: () => Message.error(i18n.t('service:login.failed.please.check.if.the.username.or.password.is.correct')),
});

const recoverRequest = createAuthRecoveryCoordinator({
  getRefreshToken: getRefeshToken,
  refreshTokens: async (refreshToken) => {
    const res = await postForm<{ accessToken?: string; refreshToken?: string }>(
      REFRESH_TOKEN_URL,
      { token: refreshToken },
      {
        flag: true,
        authMode: 'none',
      },
    );
    return { accessToken: res?.accessToken || '', refreshToken: res?.refreshToken || '' };
  },
  saveTokens: ({ accessToken, refreshToken }) => {
    setAccessToken(accessToken);
    setRefeshToken(refreshToken);
  },
  invalidateSession,
});

function request(config: IRequest) {
  return instance(config)
    .then((res: any = {}) => {
      return res;
    })
    .catch((err) => {
      return Promise.reject(err);
    });
}

export function postJSON<T>(url: string, data?: object, config?: IRequest): Promise<T> {
  return request({
    method: 'post',
    url,
    data,
    ...config,
    headers: {
      'Content-Type': 'application/json',
    },
  });
}

export function postForm<T>(url: string, data?: object, config?: IRequest): Promise<T> {
  return request({
    method: 'post',
    url,
    data: querystring.stringify(data),
    ...config,
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
  });
}

export function get<T>(url: string, params?: Record<string, any>, config?: IRequest): Promise<T> {
  if (params) {
    url = `${url}?${querystring.stringify(params)}`;
  }
  return request({
    method: 'get',
    url,
    ...config,
  });
}

export function patchJSON<T>(url: string, data?: object, config?: IRequest): Promise<T> {
  return request({
    method: 'patch',
    url,
    data,
    ...config,
    headers: {
      'Content-Type': 'application/merge-patch+json', // 资源的部分
    },
  });
}

export function patchJsonPatch<T>(url: string, data?: object, config?: IRequest): Promise<T> {
  return request({
    method: 'patch',
    url,
    data,
    ...config,
    headers: {
      'Content-Type': 'application/json-patch+json', // 必须是操作数组
    },
  });
}

export function patchForm<T>(url: string, data?: object, config?: IRequest): Promise<T> {
  return request({
    method: 'patch',
    url,
    data: querystring.stringify(data),
    ...config,
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
  });
}

export function putJSON<T>(url: string, data?: object, config?: IRequest): Promise<T> {
  return request({
    method: 'put',
    url,
    data,
    ...config,
    headers: {
      'Content-Type': 'application/json',
    },
  });
}

export function delJSON<T>(url: string, data?: object, config?: IRequest): Promise<T> {
  return request({
    method: 'delete',
    url,
    data,
    ...config,
    headers: {
      'Content-Type': 'application/json',
    },
  });
}

export function putForm<T>(url: string, data?: object, config?: IRequest): Promise<T> {
  return request({
    method: 'put',
    url,
    data: querystring.stringify(data),
    ...config,
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
  });
}

export function deleteRes<T>(url: string, params?: Record<string, any>, config?: IRequest): Promise<T> {
  if (params) {
    url = `${url}?${querystring.stringify(params)}`;
  }
  return request({
    method: 'delete',
    url,
    ...config,
  });
}

export async function getBatchData(requestList, limit, callBack, errCb = (err) => {}) {
  const pool = new Set();
  for (const request of requestList) {
    if (pool.size >= limit) {
      await Promise.race(pool).then(callBack).catch(errCb);
    }
    const promise = request();
    const cb = () => {
      pool.delete(promise);
    };
    promise.then(cb, cb);
    pool.add(promise);
  }
  Promise.allSettled(pool)
    .then((res) => {
      //NOSONAR
      res.forEach((item: any) => {
        callBack(item.value);
      });
    })
    .catch(errCb);
}

export default instance;
